import React, { useEffect, useMemo } from 'react'
import dynamic from 'next/dynamic'
import { BRAND, PLP_Items_Per_Page } from '@/data/constants'

import usePLPAnalytics from '@/hooks/usePLPAnalytics'
import { useClientSideCartAndUser, SiteWideSSRProps } from '@/services/getSiteWideInitialProps'

import type { CategoryPageBannerContentfulResponse, Product, QueryParamsType } from '@/types'
import type { BreadcrumbsType, CategoryESresponse, FacetsType } from '@/types/ElasticSearch/SearchAPIResponse'
import type { NextPage } from 'next'
import { log } from '@/services/Log'
import allPromisesWithRetries from '@/helpers/allPromisesWithRetries'

const Default = dynamic(import('@/layouts/default'))
const PLP = dynamic(import('@/components/Plp/PLP'))
interface BrandPageProps extends SiteWideSSRProps {
  legacyId: string,
  slug: string,
  query: QueryParamsType,
  path: string,
  currentPage: number,
  total: number,
  content: CategoryPageBannerContentfulResponse | null,
  category: CategoryESresponse | null,
  products: Product[],
  facets: FacetsType[],
  breadcrumbs: BreadcrumbsType[],
  itemsPerPage: number,
  sort?: string | string[],
  userAgent: string,
}

const BrandPage: NextPage<BrandPageProps> = ({
  content,
  breadcrumbs,
  category,
  total,
  legacyId,
  slug,
  query,
  facets,
  products,
  currentPage,
  sitewideBanner,
  megaNav,
  itemsPerPage,
  sort,
  userAgent,
}) => {
  const { cart, setCart, user } = useClientSideCartAndUser()

  const categoryName = category?.name || ''
  const { PLPAnalytics } = usePLPAnalytics({
    plpType: 'brand',
    cart,
    customer: user,
    products,
    categoryName,
  })

  useEffect(() => {
    PLPAnalytics()
  }, [products, cart, PLPAnalytics])

  const metaTags = useMemo(() => {
    const description = breadcrumbs.slice(0).reverse().map(({ name }) => name).concat(BRAND)
      .join(' | ')
    const title = `${categoryName} | Brands | One Kings Lane`
    return { description, title }
  }, [breadcrumbs, categoryName])

  const baseHref = `/brand?slug=${slug}&legacyId=${legacyId}&`
  const baseUrl = typeof window !== 'undefined' ? `${window.location.pathname}?` : ''

  return (
    <Default
      user={user}
      sitewideBanner={sitewideBanner}
      megaNav={megaNav}
      meta={metaTags}
      cart={cart}
      onUpdateCart={setCart}
    >
      <PLP
        itemsPerPage={itemsPerPage}
        baseHref={baseHref}
        baseUrl={baseUrl}
        currentPage={currentPage}
        products={products}
        showBreadcrumbs={!(content?.hideBreadcrumbs)}
        breadcrumbs={breadcrumbs}
        PLPHeader={categoryName}
        totalNumberOfProducts={total}
        facets={facets}
        queryParam={query}
        plpBanner={content}
        shouldUseMerchandising={currentPage === 1 && sort === 'ourPicks'}
        userAgent={userAgent}
        isCategoryEvent
      />
    </Default>
  )
}

const getQueryWithValidCharacters = (queryWithoutSlug: {
  [key: string]: string | string[] | undefined
}): { [key: string]: string } => {
  const LEGAL_FILTER_CHARACTER_SET = /^[\w \-+,%$'"&]+$/
  const hasIllegalChar = (str: string) => !LEGAL_FILTER_CHARACTER_SET.test(str)
  const deleteKey = (key: string) => delete queryWithoutSlug[key]
  Object.keys(queryWithoutSlug).forEach((key) => {
    const value = queryWithoutSlug[key]
    if (!value) {
      deleteKey(key)
    } else if (Array.isArray(value)) {
      deleteKey(key)
    } else if (hasIllegalChar(value)) {
      deleteKey(key)
    }
  })
  return (queryWithoutSlug as { [key: string]: string })
}

BrandPage.getInitialProps = async (ctx) => {
  const {
    req, res, query, asPath,
  } = ctx
  let userAgent = ''
  const legacyId = Array.isArray(query?.legacyId) ? query?.legacyId?.[0] : (query?.legacyId || '')

  const { slug, ...queryWithoutSlug } = query
  const legalQuery = getQueryWithValidCharacters(queryWithoutSlug)
  const path = `${asPath || ''}`.replace(/^(\/c-[a-z-]+)\//, '$1')
  log(`Category Page getServerSideProps ${JSON.stringify({ path, asPath })}`)
  const currentPage = parseInt(`${String(query?.page || '')}`) || 1
  try {
    const itemsPerPageFromQueryParam = query.limit
    const itemsPerPage = (
      (
        typeof itemsPerPageFromQueryParam === 'string'
          ? parseInt(itemsPerPageFromQueryParam)
          : 0
      ) || PLP_Items_Per_Page
    )
    const [
      { Search },
      { Content },
      { getSiteWideInitialProps },
      { getWindowAgent },
    ] = await allPromisesWithRetries(() => [
      import('@/services/Search'),
      import('@/services/Content'),
      import('@/services/getSiteWideInitialProps'),
      import('@/helpers/common'),
    ])
    userAgent = getWindowAgent(req) || userAgent
    const [
      siteWideInitialProps,
      response,
      content,
    ] = await Promise.all([
      getSiteWideInitialProps(ctx),
      Search.category({
        path, page: currentPage, legacyId, limit: itemsPerPage,
      }),
      await Content.categoryPage({ id: legacyId }),
    ])

    if (!response) {
      throw new Error('No response from search endpoint!')
    }
    const {
      category, facets, products = [], breadcrumbs, total,
    } = response

    return {
      legacyId,
      slug: (slug as string),
      content,
      category,
      query: legalQuery,
      ...siteWideInitialProps,
      products,
      facets,
      breadcrumbs,
      path,
      total,
      currentPage,
      itemsPerPage,
      sort: queryWithoutSlug.sort,
      userAgent,
    }
  } catch (e) {
    log('Cannot get initial props in category page, redirecting', e)
    res?.writeHead(302, {
      Location: '/',
    })
    res?.end()
  }
  const [{ SiteWideInitialPropsFailure }] = await allPromisesWithRetries(() => [import('@/services/getSiteWideInitialProps')])
  return {
    legacyId,
    slug: (slug as string),
    total: 0,
    content: null,
    query: legalQuery,
    path,
    currentPage,
    category: null,
    products: [],
    facets: [],
    breadcrumbs: [],
    itemsPerPage: PLP_Items_Per_Page,
    ...SiteWideInitialPropsFailure,
    sort: queryWithoutSlug.sort,
    userAgent,
  }
}
export default BrandPage
